import {
  AddIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  CrossIcon,
  ExportIcon,
  FilterIcon,
  Heading,
  IconButton,
  Menu,
  Pane,
  Paragraph,
  Popover,
  Position,
  SideSheet,
  Spinner,
  Table,
  TextDropdownButton,
  TextInputField,
  toaster,
  Tooltip,
} from "evergreen-ui";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CPagination } from "@coreui/react";
import useInput from "src/Hooks/useInput";
import { useMutation, useQuery } from "@apollo/client";
import { GET_ALL_PACKAGE_ORDERS } from "./AdminRestockQueries";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { exportToCsv, getDateWithTime } from "src/utilities";
import AdminRestockOrderRow from "src/Components/AdminRestockOrderRow";
import UserSelector from "src/Components/UserSelector";
import PackageSelector from "src/Components/PackageSelector";
import { INSERT_ONE_ORDER } from "./AdminRestockMutation";
import { NewPackageOrderType } from "src/Types/data-types";
import Refresh from "src/Components/Refresh";

const Container = styled.div`
  ${(props) => props.theme.pageContainer}
`;

const LoadingContainer = styled.div`
  ${(props) => props.theme.loadingContainer}
`;

const Title = styled.h1`
  ${(props) => props.theme.title};
  margin-bottom: 1rem;
  width: 100%;
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
  @media (max-width: ${(props) => props.theme.maxWidth}) {
    flex-direction: column;
    justify-content: space-around;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: inherit;
  width: 100%;
  justify-content: flex-end;
  @media (max-width: ${(props) => props.theme.maxWidth}) {
    align-items: center;
  }
`;

interface IProps extends RouteComponentProps<any> {}
const AdminRestockOrders: React.SFC<IProps> = ({ match, history }) => {
  let limit = 10;
  let currentPage = match.params.page || 1;
  if (currentPage <= 0) {
    history.push("/admin/restock_orders/1");
  }
  const [page, setPage] = useState(currentPage);
  const searchValue = useInput("");
  let searchQuery = `${searchValue.value}`;
  const [showColumn, setShowColumn] = useState("create_date");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPackage, setSelectedPackage] = useState("all");
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const quantityInput = useInput(0);
  const companyOfferInput = useInput(0);
  const [
    newOrderPackage,
    setNewOrderPackage,
  ] = useState<NewPackageOrderType | null>(null);
  const [newBuyerId, setNewBuyerId] = useState("");
  const [newSellerId, setNewSellerId] = useState("");
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [insertOneOrder] = useMutation(INSERT_ONE_ORDER);
  const { data, loading, error, refetch } = useQuery(GET_ALL_PACKAGE_ORDERS, {
    variables: {
      offset: limit * (currentPage - 1) < 0 ? 1 : limit * (currentPage - 1),
      limit,
      where: {
        _and: [
          onlyAdmin
            ? {
                _or: [
                  {
                    seller_id: {
                      _is_null: true,
                    },
                  },
                  {
                    seller: {
                      upline: {
                        _is_null: true,
                      },
                    },
                  },
                  {
                    order_package: {
                      pay_company: {
                        _eq: true,
                      },
                    },
                  },
                ],
              }
            : {},
          selectedStatus === "all"
            ? {}
            : {
                status: {
                  _ilike: selectedStatus,
                },
              },
          {
            order_package: {
              id:
                selectedPackage !== "all"
                  ? {
                      _eq: selectedPackage,
                    }
                  : {},
            },
          },
          {
            id:
              isNaN(searchValue.value) || searchValue.value === ""
                ? {
                    _is_null: false,
                  }
                : {
                    _eq: parseInt(searchValue.value),
                  },
          },
          {
            _or: [
              {
                user: {
                  name: {
                    _ilike: searchQuery,
                  },
                },
              },
              {
                user: {
                  username: {
                    _ilike: searchQuery,
                  },
                },
              },
              {
                seller: {
                  name: {
                    _ilike: searchQuery,
                  },
                },
              },
              {
                seller: {
                  username: {
                    _ilike: searchQuery,
                  },
                },
              },
              {
                id: isNaN(searchValue.value)
                  ? {
                      _eq: 0,
                    }
                  : {
                      _eq: parseInt(searchValue.value),
                    },
              },
            ],
          },
        ],
      },
    },
  });
  const columnOptions = [
    {
      label: "ID",
      value: "id",
    },
    {
      label: "Bonus Period",
      value: "bonus_period",
    },
    {
      label: "Payment",
      value: "payment",
    },
    {
      label: "Upline",
      value: "upline",
    },
    {
      label: "Create Date",
      value: "create_date",
    },
  ];

  const statusOptions = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Pending",
      value: "Pending",
    },
    {
      label: "Approved",
      value: "Approved",
    },
    {
      label: "Rejected",
      value: "Rejected",
    },
    {
      label: "Cancelled",
      value: "Cancelled",
    },
  ];

  const toggleShowAdvancedFilter = () => {
    setShowAdvancedFilter(!showAdvancedFilter);
  };

  const exportData = (e) => {
    e.preventDefault();
    let csv =
      "ID,BUYER,QUANTITY,PACKAGE,CREATE DATE,STATUS,UPLINE,BONUS PERIOD,PAYMENT\n";

    data.allResults.map((pac) => {
      return (csv =
        csv +
        `${pac.id},${pac.user.name} (${pac.user.username}),${pac.quantity} + ${
          pac.system_offer
        },${pac.order_package.chinese_name} | ${
          pac.order_package.name
        },${getDateWithTime(pac.created_at)},${pac.status},${
          pac.seller
            ? pac.seller.name + `(${pac.seller.username})`
            : "NO UPLINE"
        },${pac.month}/${pac.year},${pac.payment_slip || "NOT PAID"}\n`);
    });

    exportToCsv(csv, "RESTOCK_ORDERS");
  };

  const pageChange = (newPage) => {
    currentPage !== newPage && history.push(`/admin/restock_orders/${newPage}`);
  };

  useEffect(() => {
    currentPage !== page && setPage(currentPage);
    if (error) {
      toaster.danger(error.message);
    }
  }, [error, currentPage, page]);

  const toggleShowNewOrder = () => {
    setShowNewOrder(!showNewOrder);
  };

  const selectPackage = (e) => {
    setNewOrderPackage(e);
  };

  const selectBuyer = (e) => {
    setNewBuyerId(e);
  };
  const selectSeller = (e) => {
    setNewSellerId(e);
  };
  const submitNewOrder = (e) => {
    e.preventDefault();
    if (newBuyerId === "") {
      return toaster.danger("Please select a buyer!");
    }
    if (newSellerId === "") {
      return toaster.danger("Please select an upline!");
    }
    if (quantityInput.value === "" || isNaN(quantityInput.value)) {
      return toaster.danger("Please enter a quantity!");
    }
    if (companyOfferInput.value === "" || isNaN(companyOfferInput.value)) {
      return toaster.danger("Please enter company offer quantity!");
    }
    if (newOrderPackage !== null) {
      setIsLoading(true);
      insertOneOrder({
        variables: {
          object: {
            buyer_id: newBuyerId,
            seller_id: newSellerId,
            selling_price: newOrderPackage.unit_price,
            cost_price: 0,
            package_id: newOrderPackage.id,
            country: newOrderPackage.package_country.id,
            quantity: quantityInput.value,
            system_offer: companyOfferInput.value,
          },
        },
      })
        .then(() => {
          toaster.success("Order created!");
          refetch().then(() => {
            setIsLoading(false);
            setShowNewOrder(false);
          });
        })
        .catch((e) => {
          toaster.danger(e.message);
          setIsLoading(false);
        });
    } else {
      return toaster.danger("Please select a package!");
    }
  };
  return (
    <Container>
      <Header>
        <Title className=" flex flex-row items-center">
          Restock Orders (
          {(!loading && data && data.allResults && data.allResults.length) || 0}
          )
          <span>
            <Refresh
              onClick={() => {
                setIsLoading(true);
                refetch().then(() => {
                  setIsLoading(false);
                });
              }}
              loading={isLoading}
            />
          </span>
        </Title>
        <ButtonGroup>
          <Button
            onClick={toggleShowNewOrder}
            appearance={"minimal"}
            iconBefore={AddIcon}
          >
            New Order
          </Button>
          <Button
            onClick={toggleShowAdvancedFilter}
            appearance={"minimal"}
            iconBefore={FilterIcon}
          >
            Advanced Filter
          </Button>
          <Button
            onClick={exportData}
            appearance={"minimal"}
            iconBefore={ExportIcon}
          >
            Export to Excel
          </Button>
        </ButtonGroup>
      </Header>
      <Table
        marginBottom={"1rem"}
        maxHeight={"90vh"}
        width="100%"
        maxWidth="calc(100vw - 2rem)"
        overflow="scroll"
      >
        <Table.Head minWidth="1080px" accountForScrollbar={true}>
          <Table.SearchHeaderCell
            {...searchValue}
            placeholder="Search ID, User Name.."
          />
          <Table.TextHeaderCell>QUANTITY</Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.Group title="Package">
                    {!loading && data ? (
                      <>
                        <Menu.Item
                          onSelect={() => {
                            setSelectedPackage("all");
                          }}
                        >
                          All
                        </Menu.Item>
                        {data.packages.map((pac, index) => (
                          <Menu.Item
                            onSelect={() => {
                              setSelectedPackage(pac.id);
                            }}
                            key={pac.id}
                          >
                            {pac.chinese_name} | {pac.name} (
                            {pac.package_country.name})
                          </Menu.Item>
                        ))}
                      </>
                    ) : (
                      <Menu.Item
                        icon={Spinner}
                        onSelect={() => {
                          setSelectedPackage("all");
                        }}
                      >
                        Loading...
                      </Menu.Item>
                    )}
                  </Menu.Group>
                </Menu>
              )}
            >
              <TextDropdownButton>
                {selectedPackage === "all"
                  ? "PACKAGE"
                  : data &&
                    data.packages &&
                    data.packages[
                      data.packages.findIndex((a) => a.id === selectedPackage)
                    ].name}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell minWidth={"140px"}>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Show"
                    options={columnOptions}
                    selected={showColumn}
                    onChange={(value) => {
                      setShowColumn(value);
                      close();
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {showColumn.replace("_", " ").toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Status"
                    options={statusOptions}
                    selected={selectedStatus}
                    onChange={(value) => {
                      setSelectedStatus(value);
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {selectedStatus === "all"
                  ? "STATUS"
                  : selectedStatus.toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell />
        </Table.Head>
        <Table.Body
          marginBottom={"1rem"}
          maxHeight={"90vh"}
          minWidth="1080px"
          overflow="scroll"
        >
          {loading && (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          )}
          {!loading && error && error.message}
          {!loading &&
            data &&
            data.paginated &&
            (data.paginated.length > 0 ? (
              data.paginated.map((order, index) => {
                let orderObject = {
                  id: order.id,
                  username: order.user.name + `(${order.user.username})`,
                  avatar: order.user.avatar,
                  actualQuantity: order.quantity,
                  actualSystemOffer: order.system_offer,
                  sellingPrice: order.selling_price,
                  packageId: order.package_id,
                  quantity: order.quantity + " + " + order.system_offer,
                  amount: order.quantity * order.selling_price,
                  bonus_period: `${order.month}/${order.year}`,
                  payment: order.payment_slip ? (
                    <Tooltip
                      key={index}
                      appearance="card"
                      content={
                        <img
                          alt={"Payment slip"}
                          onClick={() => {
                            window.open(order.payment_slip);
                          }}
                          src={order.payment_slip}
                          style={{
                            maxWidth: "100vw",
                            maxHeight: "100vh",
                            objectFit: "contain",
                          }}
                          height="auto"
                          width="auto"
                        />
                      }
                    >
                      <Badge cursor="pointer" color="green">
                        VIEW SLIP
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Badge color={"red"}>UNPAID</Badge>
                  ),
                  upline: order.seller ? (
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        marginRight={".2rem"}
                        name={order.seller.name}
                        src={order.seller.avatar}
                        size={24}
                      />
                      {order.seller.name} ({order.seller.username})
                    </span>
                  ) : (
                    "NO UPLINE"
                  ),
                  buyerId: order.user.id,
                  sellerId: order.seller ? order.seller.id : null,
                  create_date: getDateWithTime(order.created_at),
                  status: order.status,
                  packageName:
                    order.order_package.chinese_name +
                    " | " +
                    order.order_package.name +
                    " (" +
                    order.order_package.package_country.name +
                    ")",
                };

                return (
                  <AdminRestockOrderRow
                    {...orderObject}
                    key={index}
                    packages={data.packages}
                    refetch={refetch}
                    showColumn={orderObject[showColumn]}
                  />
                );
              })
            ) : (
              <Table.TextCell>No record found!</Table.TextCell>
            ))}
        </Table.Body>
      </Table>
      {!loading && data && data.package_order_aggregate && (
        <CPagination
          pages={Math.ceil(
            data.package_order_aggregate.aggregate.count / limit
          )}
          activePage={parseInt(page)}
          limit={5}
          dots={true}
          onActivePageChange={pageChange}
        />
      )}
      <SideSheet
        isShown={showAdvancedFilter}
        containerProps={{
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        }}
        onCloseComplete={() => {
          setShowAdvancedFilter(false);
        }}
      >
        <Pane
          padding="1rem"
          backgroundColor="white"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection={"row"}
        >
          <Pane maxWidth="75vw">
            <Heading marginBottom="1rem">Advanced Filter</Heading>
          </Pane>
          <IconButton icon={CrossIcon} onClick={toggleShowAdvancedFilter} />
        </Pane>
        <Pane flex={1} padding="0.5rem" background="tint1">
          <Card backgroundColor="white" padding="1rem">
            <Checkbox
              checked={onlyAdmin}
              onChange={(e) => {
                setOnlyAdmin(e.target.checked);
              }}
              label="Show only requires admin approval"
            />
            <p>Other advanced filters coming soon! (e.g. Date filter)</p>
          </Card>
        </Pane>
      </SideSheet>
      <SideSheet
        isShown={showNewOrder}
        containerProps={{
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        }}
        onCloseComplete={() => {
          setShowNewOrder(false);
        }}
      >
        <Pane
          padding="1rem"
          backgroundColor="white"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection={"row"}
        >
          <Pane maxWidth="75vw">
            <Heading marginBottom="1rem">New Order</Heading>
            <Paragraph width="auto">
              **Becareful when adding orders manually. This will affect users
              and their uplines' stock balance.
            </Paragraph>
          </Pane>
          <IconButton icon={CrossIcon} onClick={toggleShowNewOrder} />
        </Pane>
        <Pane flex={1} padding="1rem" background="tint1">
          <Card backgroundColor="white">
            <Pane padding={"1rem"}>
              <UserSelector onSelect={selectBuyer} label="Buyer" />
              <UserSelector onSelect={selectSeller} label="Upline" />

              <TextInputField label="Quantity" {...quantityInput} />
              <TextInputField label="Company Offer" {...companyOfferInput} />
              <PackageSelector
                label="Select a package"
                onSelect={selectPackage}
              />
              <Button
                marginTop="1rem"
                intent="none"
                appearance="primary"
                onClick={submitNewOrder}
                isLoading={isLoading}
              >
                New Order
              </Button>
            </Pane>
            <Pane padding="1rem" background="tint1"></Pane>
          </Card>
        </Pane>
      </SideSheet>
    </Container>
  );
};

export default withRouter(AdminRestockOrders);
